import { formatRelative } from "../OrgOverview/format";
import {
  formatDuration,
  formatRate,
  getGoldenLoanId,
  getJudgeVerdict,
  getLoanName,
  getRunLoans,
  getRunMetric,
  getSuiteRunId,
  normalizeStatus,
} from "./goldenEval";

function StatusPill({ status }) {
  const normalized = normalizeStatus(status);

  return (
    <span className={`icr-pill icr-pill--${normalized}`}>
      {normalized.replace(/-/g, " ")}
    </span>
  );
}

function metric(run, keys, fallback) {
  return getRunMetric(run, keys, fallback);
}

function getLoanResultStatus(loan) {
  const status = loan?.status || loan?.evaluationStatus;
  const verdict = getJudgeVerdict(loan);
  if (status && !["complete", "completed"].includes(normalizeStatus(status))) {
    return status;
  }
  if (loan?.isRegression || loan?.regression) {
    return "Regression";
  }

  return verdict;
}

function isAgreement(loan) {
  const agreement = loan?.evaluation?.addyVsHarness?.agreement;
  if (typeof agreement === "boolean") {
    return agreement;
  }
  const verdict = normalizeStatus(getJudgeVerdict(loan));
  if (verdict.includes("disagree") || verdict.includes("difference")) {
    return false;
  }
  if (verdict.includes("agree") || verdict.includes("pass")) {
    return true;
  }

  return null;
}

function isRegressionAgainst(previousRun, loan) {
  if (!previousRun || !loan) {
    return false;
  }
  const id = getGoldenLoanId(loan);
  const previous = getRunLoans(previousRun).find(
    (candidate) => getGoldenLoanId(candidate) === id,
  );

  return isAgreement(previous) === true && isAgreement(loan) === false;
}

function matrixTone(status) {
  const normalized = normalizeStatus(status);
  if (
    normalized.includes("agree") ||
    normalized.includes("pass") ||
    normalized === "complete"
  ) {
    return "pass";
  }
  if (normalized.includes("regression") || normalized.includes("fail")) {
    return "fail";
  }
  if (normalized.includes("running") || normalized.includes("queued")) {
    return "pending";
  }

  return "review";
}

export default function GoldenSuiteOverview({
  runs,
  loading,
  selectedSuiteRunId,
  onSelectRun,
  onRefresh,
}) {
  const selectedRun =
    runs.find((run) => getSuiteRunId(run) === selectedSuiteRunId) || runs[0];
  const selectedRunIndex = runs.indexOf(selectedRun);
  const previousRun = runs[selectedRunIndex + 1];
  const allLoans = new Map();
  runs.forEach((run) => {
    getRunLoans(run).forEach((loan) => {
      const id = getGoldenLoanId(loan) || getLoanName(loan);
      if (!allLoans.has(id)) {
        allLoans.set(id, loan);
      }
    });
  });

  const loanCount = metric(selectedRun, ["loanCount", "totalCount"], 0);
  const completedCount = metric(
    selectedRun,
    ["completedCount", "completeCount"],
    0,
  );
  const failedCount = metric(selectedRun, ["failedCount"], 0);
  const reportedRegressions = metric(
    selectedRun,
    ["regressionCount", "regressions"],
    null,
  );
  const regressions =
    typeof reportedRegressions === "number"
      ? reportedRegressions
      : previousRun
        ? getRunLoans(selectedRun).filter((loan) =>
            isRegressionAgainst(previousRun, loan),
          ).length
        : null;

  const kpis = [
    { label: "Loans", value: loanCount },
    {
      label: "Completed / failed",
      value: `${completedCount} / ${failedCount}`,
    },
    {
      label: "Addy ↔ Harness",
      value: formatRate(
        metric(selectedRun, [
          "addyHarnessAgreement",
          "addyHarnessAgreementRate",
          "agreementRate",
        ]),
      ),
    },
    {
      label: "Human accuracy",
      value: formatRate(
        metric(selectedRun, ["humanAccuracy", "humanAccuracyRate"]),
      ),
      note: "Only labeled loans",
    },
    {
      label: "Regressions",
      value: typeof regressions === "number" ? regressions : "—",
    },
    {
      label: "Duration",
      value: formatDuration(
        metric(selectedRun, ["durationMs", "elapsedMs"]),
      ),
    },
  ];

  return (
    <section className="icr-eval-section" aria-labelledby="suite-overview-title">
      <div className="icr-eval-section__header">
        <div>
          <span className="icr-eval-eyebrow">Latest 10 runs</span>
          <h2 id="suite-overview-title">Suite overview</h2>
          <p>
            Track agreement, optional human accuracy, regressions, and progress
            across repeatable income evaluations.
          </p>
        </div>
        <button className="icr-btn" type="button" onClick={onRefresh}>
          {loading ? "Refreshing…" : "Refresh suite runs"}
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="icr-eval-empty">
          <strong>No suite runs yet</strong>
          <p>Run the golden dataset to establish the first evaluation baseline.</p>
        </div>
      ) : (
        <>
          <div className="icr-eval-kpis" aria-label="Selected suite run metrics">
            {kpis.map((item) => (
              <div className="icr-eval-kpi" key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                {item.note && <small>{item.note}</small>}
              </div>
            ))}
          </div>

          <div className="icr-suite-run-strip" aria-label="Recent suite runs">
            {runs.map((run, index) => {
              const id = getSuiteRunId(run);
              const count = metric(run, ["loanCount", "totalCount"], 0);
              const done =
                metric(run, ["completedCount", "completeCount"], 0) +
                metric(run, ["failedCount"], 0);
              const progress = count ? Math.min(100, (done / count) * 100) : 0;

              return (
                <button
                  type="button"
                  key={id || index}
                  className={
                    id === selectedSuiteRunId
                      ? "icr-suite-run-card is-active"
                      : "icr-suite-run-card"
                  }
                  aria-pressed={id === selectedSuiteRunId}
                  onClick={() => onSelectRun(id)}
                >
                  <span className="icr-suite-run-card__top">
                    <b>Run {runs.length - index}</b>
                    <StatusPill status={run.status} />
                  </span>
                  <small>{formatRelative(run.createdAt || run.startedAt)}</small>
                  <span
                    className="icr-suite-progress"
                    role="progressbar"
                    aria-label={`${done} of ${count} loans finished`}
                    aria-valuemin="0"
                    aria-valuemax={count}
                    aria-valuenow={done}
                  >
                    <span style={{ width: `${progress}%` }} />
                  </span>
                  <em>
                    {done}/{count || "—"} finished
                  </em>
                </button>
              );
            })}
          </div>

          <div className="icr-suite-matrix-wrap">
            <div className="icr-suite-matrix__intro">
              <h3>Loan-by-suite matrix</h3>
              <p>Every cell includes a text verdict; color is only a secondary cue.</p>
            </div>
            {allLoans.size === 0 ? (
              <p className="icr-muted">
                Loan-level results are not available in these run summaries yet.
                Open a suite run to load its detail.
              </p>
            ) : (
              <div className="icr-table-scroll">
                <table className="icr-suite-matrix">
                  <thead>
                    <tr>
                      <th>Golden loan</th>
                      {runs.map((run, index) => (
                        <th key={getSuiteRunId(run) || index}>
                          Run {runs.length - index}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(allLoans.entries()).map(([loanId, representative]) => (
                      <tr key={loanId}>
                        <th scope="row">
                          {getLoanName(representative)}
                          <small>{loanId}</small>
                        </th>
                        {runs.map((run, index) => {
                          const result = getRunLoans(run).find(
                            (loan) =>
                              (getGoldenLoanId(loan) || getLoanName(loan)) ===
                              loanId,
                          );
                          const isRegression = isRegressionAgainst(
                            runs[index + 1],
                            result,
                          );
                          const status = result
                            ? isRegression
                              ? "Regression"
                              : getLoanResultStatus(result)
                            : "Not included";
                          const tone = result ? matrixTone(status) : "empty";

                          return (
                            <td key={getSuiteRunId(run) || index}>
                              <button
                                type="button"
                                className={`icr-matrix-cell icr-matrix-cell--${tone}`}
                                onClick={() => onSelectRun(getSuiteRunId(run))}
                                aria-label={`${getLoanName(
                                  representative,
                                )}, run ${runs.length - index}: ${status}`}
                              >
                                <span aria-hidden="true">
                                  {tone === "pass"
                                    ? "✓"
                                    : tone === "fail"
                                      ? "!"
                                      : tone === "empty"
                                        ? "—"
                                        : "•"}
                                </span>
                                {status}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
